import { useEffect, useRef, useState } from 'react';
import { Helmet } from "react-helmet-async";
import { Container } from '@mui/material';
import { usePageTransition } from '../context/PageTransitionContext.tsx';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './homepage.scss';

gsap.registerPlugin(ScrollTrigger);

// Progetti da mostrare nella homepage
const featuredProjects = [
  {
    id: '01',
    title: 'La Selleria della Bassa',
    category: 'E-commerce',
    year: '2026',
    slug: 'selleria',
    image: '/lavori-selezionati/selleria.png'
  },
  {
    id: '02',
    title: 'Circolo Ippico Charly',
    category: 'Sito Vetrina',
    year: '2026',
    slug: 'charly',
    image: '/lavori-selezionati/charly.png'
  },
  {
    id: '03',
    title: 'Marumars',
    category: 'Digital Communication',
    year: '2026',
    slug: 'marumars',
    image: '/lavori-selezionati/marumars.png'
  },
  {
    id: '04',
    title: 'Monferrato Slow Stay',
    category: 'B&B',
    year: '2026',
    slug: 'monferrato',
    image: '/lavori-selezionati/monferrato.png'
  },
  {
    id: '05',
    title: 'Fabbri Agricoltura',
    category: 'Corporate',
    year: '2026',
    slug: 'fabbri',
    image: '/lavori-selezionati/fabbri.png'
  },
  {
    id: '06',
    title: 'Ru Grafica',
    category: 'Portfolio',
    year: '2026',
    slug: 'rugrafica',
    image: '/lavori-selezionati/rugrafica.png'
  },
  {
    id: '07',
    title: 'Voltaren Studio',
    category: 'Studio di Registrazione',
    year: '2025',
    slug: 'voltaren',
    image: '/lavori-selezionati/voltaren.png'
  },
  {
    id: '08',
    title: 'Sacco',
    category: 'Corporate',
    year: '2023',
    slug: 'sacco',
    image: '/lavori-selezionati/sacco.jpg'
  },
  {
    id: '09',
    title: 'Carpenteria Maifredi',
    category: 'Corporate',
    year: '2023',
    slug: 'carpenteria-maifredi',
    image: '/lavori-selezionati/carpenteria.jpg'
  }
];

const services = [
  {
    icon: 'W',
    title: 'Web Design & Development',
    description: 'Siti web custom in React, performanti e ottimizzati SEO. Niente template, solo codice scritto su misura per te.'
  },
  {
    icon: 'U',
    title: 'UI/UX Design',
    description: 'Interfacce intuitive che guidano l\'utente verso la conversione. Design system completi e prototipi interattivi.'
  },
  {
    icon: 'A',
    title: 'Web App & Gestionali',
    description: 'Applicazioni web su misura per gestire il tuo business: dashboard, CRM, gestionali e automazioni. Tutto in React.'
  },
  {
    icon: 'C',
    title: 'CMS Custom',
    description: 'Il mio CMS proprietario in React. Tu gestisci i contenuti, io il codice. Esclusivo, veloce, su misura.'
  }
];

function Homepage() {
  const { navigateWithTransition } = usePageTransition();
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Refs scroll orizzontale
  const workSectionRef = useRef<HTMLElement>(null);
  const projectsTrackRef = useRef<HTMLDivElement>(null);

  // Animazione contatori
  const statsRef = useRef<HTMLDivElement>(null);
  const [counters, setCounters] = useState({ progetti: 0, anni: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);

  // Effetto animazione contatori
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);

            const targets = { progetti: 50, anni: 5 };
            const duration = 2000;
            const startTime = Date.now();

            const animate = () => {
              const elapsed = Date.now() - startTime;
              const progress = Math.min(elapsed / duration, 1);

              // Funzione easing (ease-out)
              const easeOut = 1 - Math.pow(1 - progress, 3);

              setCounters({
                progetti: Math.floor(easeOut * targets.progetti),
                anni: Math.floor(easeOut * targets.anni)
              });

              if (progress < 1) {
                requestAnimationFrame(animate);
              }
            };

            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  // Horizontal scroll animation for projects
  useEffect(() => {
    if (!workSectionRef.current || !projectsTrackRef.current) return;

    const track = projectsTrackRef.current;
    const section = workSectionRef.current;

    // Calculate how much to scroll horizontally
    const getScrollAmount = () => {
      const trackWidth = track.scrollWidth;
      const viewportWidth = window.innerWidth;
      return -(trackWidth - viewportWidth + 100); // 100px extra padding
    };

    const tween = gsap.to(track, {
      x: getScrollAmount,
      ease: "none",
      scrollTrigger: {
        trigger: section,
        start: "top 20%",
        end: () => `+=${track.scrollWidth - window.innerWidth + 100}`,
        pin: true,
        scrub: 1,
        invalidateOnRefresh: true,
      }
    });

    return () => {
      tween.kill();
    };
  }, []);

  useEffect(() => {
    // Hero title animation
    if (heroTitleRef.current) {
      const lines = heroTitleRef.current.querySelectorAll('.hero-line');
      gsap.fromTo(lines,
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1.2,
          stagger: 0.15,
          ease: "power3.out",
          delay: 0.3
        }
      );
    }

    // Parallax effect
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      if (heroContentRef.current) {
        heroContentRef.current.style.transform = `translateY(${scrolled * 0.3}px)`;
        heroContentRef.current.style.opacity = `${1 - scrolled * 0.002}`;
      }
      if (videoRef.current) {
        videoRef.current.style.transform = `translateY(${scrolled * 0.5}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Animate elements on scroll
    const cards = document.querySelectorAll('.project-card, .service-card');
    cards.forEach((card) => {
      gsap.fromTo(card,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none"
          }
        }
      );
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Web Designer Freelance Modena | Siti Web Professionali da €1.500</title>
        <meta name="description" content="Creo siti web professionali per PMI a Modena, Bologna e Milano. Prezzi chiari da €1.500, codice custom in React. 50+ progetti completati. Preventivo gratuito in 24h." />
        <meta name="keywords" content="web designer freelance, creazione siti web, realizzazione siti web, UI UX designer, web developer freelance, siti web professionali, web designer Modena, web designer Bologna, restyling sito web" />
        <meta name="author" content="Camilla Cino" />
        <meta property="og:title" content="Web Designer Freelance Modena | Siti Web Professionali da €1.500" />
        <meta property="og:description" content="Creo siti web professionali per PMI a Modena, Bologna e Milano. Prezzi chiari da €1.500, codice custom in React. 50+ progetti completati." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.camillacinodesigndev.it/loghi/sito/preview.png" />
        <meta property="og:url" content="https://www.camillacinodesigndev.it/" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.camillacinodesigndev.it/" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Camilla Cino - Web Designer Freelance",
            "description": "Web designer e developer freelance specializzata in creazione siti web, UI/UX design e sviluppo front-end con React",
            "url": "https://www.camillacinodesigndev.it",
            "logo": "https://www.camillacinodesigndev.it/loghi/sito/preview.png",
            "telephone": "+39-3482659545",
            "email": "info@camillacinodesigndev.it",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Modena",
              "addressRegion": "Emilia-Romagna",
              "addressCountry": "IT"
            },
            "areaServed": ["Modena", "Bologna", "Milano", "Verona", "Torino"],
            "priceRange": "$$",
            "serviceType": ["Creazione Siti Web", "Web Design", "UI/UX Design", "Sviluppo Front-end"]
          })}
        </script>
      </Helmet>

      <div className="homepage-new">
        {/* Hero Section */}
        <section className="hero-section">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            suppressHydrationWarning
          >
            <source src="/video/video2.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-bg"></div>
          <Container maxWidth="xl" ref={heroContentRef} className="hero-content">
            <div className="hero-label">
              <span className="label-line"></span>
              Digital Designer & Developer
            </div>

            <h1 className="hero-title" ref={heroTitleRef}>
              <span className="hero-line"><span className="gradient-text">Creo</span> esperienze</span>
              <span className="hero-line">digitali che</span>
              <span className="hero-line"><span className="outline-text">lasciano</span> il segno</span>
            </h1>

            <p className="hero-description">
              Design & Development su misura. Trasformo idee in prodotti digitali
              che funzionano, convertono e vengono ricordati.
            </p>

            <button
              className="hero-cta"
              onClick={() => navigateWithTransition('/lavori')}
            >
              Scopri i progetti
              <span className="arrow">→</span>
            </button>
          </Container>

          {/* Stats - fuori dal container, a lato destro */}
          <div className="hero-stats" ref={statsRef} suppressHydrationWarning>
            <div className="stat-item" suppressHydrationWarning>
              <div className="stat-number" suppressHydrationWarning>{counters.progetti}+</div>
              <div className="stat-label">Progetti</div>
            </div>
            <div className="stat-item" suppressHydrationWarning>
              <div className="stat-number" suppressHydrationWarning>{counters.anni}+</div>
              <div className="stat-label">Anni Exp.</div>
            </div>
          </div>

          <div className="scroll-indicator">
            <div className="scroll-line"></div>
            <span>Scroll</span>
          </div>
        </section>

        {/* Work Section */}
        <section className="work-section" id="work" ref={workSectionRef}>
          <Container maxWidth="xl">
            <div className="section-header">
              <h2 className="section-title">
                <span className="number">01</span>
                Lavori Selezionati
              </h2>
              <button
                className="view-all"
                onClick={() => navigateWithTransition('/lavori')}
              >
                Vedi tutti →
              </button>
            </div>
          </Container>

          <div className="projects-wrapper">
            <div className="projects-track" ref={projectsTrackRef}>
              {featuredProjects.map((project) => (
                <div
                  key={project.id}
                  className="project-card"
                  onClick={() => navigateWithTransition('/lavori')}
                >
                  <div className="project-image" style={{ backgroundImage: `url(${project.image})` }}>
                    <span className="project-number">{project.id}</span>
                  </div>
                  <div className="project-info">
                    <div className="project-category">{project.category}</div>
                    <h3 className="project-title">{project.title}</h3>
                    <span className="project-year">{project.year}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Marquee Section */}
        <section className="marquee-section">
          <div className="marquee">
            <div className="marquee-content">
              <span className="marquee-item">DESIGN</span>
              <span className="marquee-item">•</span>
              <span className="marquee-item">DEVELOP</span>
              <span className="marquee-item">•</span>
              <span className="marquee-item">DELIVER</span>
              <span className="marquee-item">•</span>
            </div>
            <div className="marquee-content">
              <span className="marquee-item">DESIGN</span>
              <span className="marquee-item">•</span>
              <span className="marquee-item">DEVELOP</span>
              <span className="marquee-item">•</span>
              <span className="marquee-item">DELIVER</span>
              <span className="marquee-item">•</span>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="services-section" id="services" style={{ backgroundImage: "url('/sfondi/sfondo2.png')" }}>
          <Container maxWidth="xl">
            <div className="section-header">
              <h2 className="section-title">
                <span className="number">02</span>
                Servizi
              </h2>
            </div>

            <div className="services-grid">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="service-card"
                  onClick={() => navigateWithTransition('/servizi')}
                >
                  <div className="service-icon">{service.icon}</div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <span className="service-cta">Scopri di più <span className="arrow">→</span></span>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <Container maxWidth="lg">
            <h2 className="cta-title">
              Hai un progetto<br />
              <span className="glitch" data-text="ambizioso?">ambizioso?</span>
            </h2>
            <button
              className="hero-cta"
              onClick={() => navigateWithTransition('/contatti')}
            >
              Parliamone
              <span className="arrow">→</span>
            </button>
          </Container>
        </section>

      </div>
    </>
  );
}

export default Homepage;
